import React from "react";
import Wizard from "@cloudscape-design/components/wizard";
import StartComponent from "./StartComponent";
import { ToolSelectionStep } from "./ToolSelectionStep";
import {
  Header,
  FormField,
  Input,
  SpaceBetween,
  Button,
  Select,
  Grid,
  TokenGroup,
  Box,
  Link,
  Alert,
  FileUpload,
} from "@cloudscape-design/components";
import { I18nProvider } from "@cloudscape-design/components/i18n";
import Slider from "@cloudscape-design/components/slider";
import FileTokenGroup from "@cloudscape-design/components/file-token-group";
import Textarea from "@cloudscape-design/components/textarea";
import { useNavigate } from "react-router-dom";

function convertArrayToObjects(arr) {
  return arr.map((item, index) => ({
    label: item,
    dismissLabel: `Remove ${item}`,
  }));
}

function getQualitySuggestions(description, assumptions) {
  const suggestions = [];

  if (!description || description.trim().length === 0) {
    suggestions.push(
      "Add a description with scope, ownership, and key data flows. This helps the AI focus on relevant threats instead of generating generic ones."
    );
  }

  if (!assumptions || assumptions.length === 0) {
    suggestions.push(
      "Add assumptions about what's already handled. This stops the AI from flagging things outside your control."
    );
  }

  return suggestions;
}

export const SubmissionComponent = ({
  onBase64Change,
  base64Content,
  iteration,
  setIteration,
  setVisible,
  handleStart,
  loading,
  reasoning,
  setReasoning,
}) => {
  const isReasoningEnabled = import.meta.env.VITE_REASONING_ENABLED === "true";
  const [activeStepIndex, setActiveStepIndex] = React.useState(0);
  const [value, setValue] = React.useState([]);
  const [title, setTitle] = React.useState("");
  const [newAssumption, setNewAssumption] = React.useState("");
  const [assumptions, setAssumptions] = React.useState([]);
  const [text, setText] = React.useState("");
  const [error, setError] = React.useState(false);
  const [selectedTools, setSelectedTools] = React.useState([]);
  const [customTools, setCustomTools] = React.useState("");
  const [smePdfFile, setSmePdfFile] = React.useState([]);  // NEW: SME PDF upload
  const [smePdfBase64, setSmePdfBase64] = React.useState(null);  // NEW: SME PDF base64
  const navigate = useNavigate();
  
  const handleAddAssumption = () => {
    if (newAssumption.trim()) {
      setAssumptions((prev) => [...prev, newAssumption.trim()]);
      setNewAssumption("");
    }
  };

  const handleKnowledgeSourcesClick = (e) => {
    e.preventDefault();
    navigate("/guides/tui-knowledge-sources");
  };

  const handleRemoveAssumption = (index) => {
    setAssumptions((prev) => prev.filter((_, i) => i !== index));
  };

  // NEW: Handle SME PDF upload
  const handleSmePdfChange = async ({ detail }) => {
    setSmePdfFile(detail.value);
    
    if (detail.value.length > 0) {
      const file = detail.value[0];
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const base64WithPrefix = e.target.result;
        const base64Value = base64WithPrefix.split(",")[1];
        setSmePdfBase64({
          type: file.type,
          value: base64Value,
          name: file.name,
        });
      };
      
      reader.onerror = (error) => {
        console.error("Error reading SME PDF:", error);
      };
      
      reader.readAsDataURL(file);
    } else {
      setSmePdfBase64(null);
    }
  };

  return (
    <Wizard
      i18nStrings={{
        stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
        collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
        skipToButtonLabel: (step, stepNumber) => `Skip to ${step.title}`,
        navigationAriaLabel: "Steps",
        previousButton: "Previous",
        nextButton: "Next",
        optional: "optional",
      }}
      onNavigate={({ detail }) => {
        if (detail.reason === "next") {
          if (!value[0] && detail.requestedStepIndex === 2) {
            setError(true);
          } else if (title.length === 0 && detail.requestedStepIndex === 1) {
            setError(true);
          } else {
            setError(false);
            setActiveStepIndex(detail.requestedStepIndex);
          }
        }
        if (detail.reason === "previous") {
          setError(false);
          setActiveStepIndex(detail.requestedStepIndex);
        }
      }}
      activeStepIndex={activeStepIndex}
      submitButtonText="Start threat modeling"
      isLoadingNextStep={loading}
      onSubmit={() => {
        const toolsList = [
          ...selectedTools.map(t => t.value),
          ...customTools.split(',').map(t => t.trim()).filter(t => t)
        ];
        handleStart(title, text, assumptions, toolsList, smePdfBase64);  // Pass SME PDF
      }}
      steps={[
        {
          title: "Title",
          description: "Provide a meaningful title for the threat model.",
          content: (
            <SpaceBetween size="m">
              <Box variant="p" color="text-body-secondary">
                This tool uses TUI-specific security standards, baseline questions, and approved tools 
                to generate comprehensive threat analysis.{" "}
                <Link variant="primary" onFollow={handleKnowledgeSourcesClick}>
                  Learn more about TUI knowledge sources
                </Link>
              </Box>
              <div style={{ minHeight: 200 }}>
                <FormField errorText={error ? "Title is required." : null}>
                  <Input value={title} onChange={(event) => setTitle(event.detail.value)} />
                </FormField>
              </div>
            </SpaceBetween>
          ),
        },
        {
          title: "Architecture diagram",
          description: "Upload 1-3 architecture diagrams (PNG/JPG). Provide multiple views (high-level, detailed, security layer) for comprehensive threat analysis. Max 5MB per file.",
          content: (
            <div style={{ minHeight: 200 }}>
              <SpaceBetween size="s">
                <Box variant="small" color="text-body-secondary">
                  Tip: Label components using TUI naming conventions (USL, MFE, BFF, MWA) for more relevant threat analysis.
                </Box>
                <StartComponent
                  onBase64Change={onBase64Change}
                  base64Content={base64Content}
                  value={value}
                  setValue={setValue}
                  error={error}
                  setError={setError}
                />
              </SpaceBetween>
            </div>
          ),
        },
        {
          title: "Description",
          description:
            "Describe what's in scope, out of scope, key data flows, and auth mechanisms. The more context you provide, the fewer irrelevant threats will be generated.",
          content: (
            <div style={{ minHeight: 200 }}>
              <FormField>
                <Textarea
                  onChange={({ detail }) => setText(detail.value)}
                  value={text}
                  placeholder={"Scope: What is your team responsible for? What system/flow are you threat modeling?\nOut of scope: What components are managed by other teams?\nData flows: What sensitive data moves through your system?\nAuth: How do users and services authenticate?"}
                  rows={6}
                />
              </FormField>
            </div>
          ),
        },
        {
          title: "Select iterations",
          description:
            "Determine the number of runs needed to generate the threat catalog. Increasing the number of runs will result in a more comprehensive and detailed threat catalog.",
          content: (
            <div style={{ minHeight: 200 }}>
              <SpaceBetween size="s">
                <FormField>
                  <Select
                    options={[
                      { label: "Auto", value: 0 },
                      { label: "1", value: 1 },
                      { label: "2", value: 2 },
                      { label: "3", value: 3 },
                      { label: "5", value: 5 },
                      { label: "7", value: 7 },
                      { label: "10", value: 10 },
                    ]}
                    selectedOption={iteration}
                    triggerVariant="option"
                    onChange={({ detail }) => setIteration(detail.selectedOption)}
                  />
                </FormField>
                <FormField
                  label="Reasoning boost"
                  description="Controls the amount of time the model spends thinking before responding."
                >
                  <Slider
                    i18nStrings={I18nProvider}
                    disabled={!isReasoningEnabled}
                    onChange={({ detail }) => setReasoning(detail.value)}
                    value={reasoning}
                    valueFormatter={(value) =>
                      [
                        { value: "0", label: "None" },
                        { value: "1", label: "Low" },
                        { value: "2", label: "Medium" },
                        { value: "3", label: "High" },
                      ].find((item) => item.value === value.toString())?.label || ""
                    }
                    ariaDescription="From None to High"
                    max={3}
                    min={0}
                    referenceValues={[1, 2]}
                    step={1}
                  />
                </FormField>
              </SpaceBetween>
            </div>
          ),
          isOptional: true,
        },
        {
          title: "Provide assumptions",
          description:
            "What security controls are already in place? What's managed by other teams? This prevents the AI from flagging things you already know about.",
          content: (
            <div style={{ minHeight: 200 }}>
              <SpaceBetween direction="vertical" size="xs">
                <Grid gridDefinition={[{ colspan: { default: 8 } }, { colspan: { default: 4 } }]}>
                  <Input
                    value={newAssumption}
                    onChange={({ detail }) => setNewAssumption(detail.value)}
                    placeholder="Type new assumption"
                  />
                  <Button onClick={handleAddAssumption} disabled={!newAssumption.trim()}>
                    Add
                  </Button>
                </Grid>
                <TokenGroup
                  items={assumptions.map((item, index) => ({
                    label: item,
                    dismissLabel: `Remove ${item}`,
                    disabled: false,
                  }))}
                  onDismiss={({ detail }) => {
                    handleRemoveAssumption(detail.itemIndex);
                  }}
                />
              </SpaceBetween>
            </div>
          ),
        },
        {
          title: "Upload SME report (optional)",
          description:
            "Upload a previous SME-reviewed threat report (PDF) to compare with AI-generated threats. The system will flag discrepancies and show SME rationale.",
          content: (
            <div style={{ minHeight: 200 }}>
              <SpaceBetween size="m">
                <Box variant="p" color="text-body-secondary">
                  If you have a past threat model reviewed by security experts, upload it here. 
                  The AI will compare its analysis with the SME report and highlight any significant differences.
                </Box>
                <FileUpload
                  accept=".pdf"
                  onChange={handleSmePdfChange}
                  value={smePdfFile}
                  i18nStrings={{
                    uploadButtonText: (e) => (e ? "Choose files" : "Choose file"),
                    dropzoneText: (e) => (e ? "Drop files to upload" : "Drop PDF to upload"),
                    removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                    limitShowFewer: "Show fewer files",
                    limitShowMore: "Show more files",
                    errorIconAriaLabel: "Error",
                  }}
                  showFileLastModified
                  showFileSize
                  tokenLimit={1}
                  constraintText="PDF format only. Used for comparison with AI-generated threats."
                />
              </SpaceBetween>
            </div>
          ),
          isOptional: true,
        },
        {
          title: "Select approved tools",
          description:
            "Choose which tools should be recommended in threat mitigations. Only selected tools will appear in the generated report.",
          content: (
            <div style={{ minHeight: 200 }}>
              <ToolSelectionStep
                selectedTools={selectedTools}
                onToolsChange={setSelectedTools}
                customTools={customTools}
                onCustomToolsChange={setCustomTools}
              />
            </div>
          ),
          isOptional: true,
        },
        {
          title: "Review and launch",
          content: (
            <div style={{ minHeight: 250 }}>
              <SpaceBetween size="xl">
                {(() => {
                  const suggestions = getQualitySuggestions(text, assumptions);
                  if (suggestions.length === 0) return null;
                  return (
                    <Alert type="warning" header="Tips for better results">
                      <SpaceBetween size="xs">
                        {suggestions.map((suggestion, idx) => (
                          <Box key={idx} variant="p">{suggestion}</Box>
                        ))}
                        <Box variant="p" color="text-body-secondary">
                          You can still proceed, but adding more context typically reduces irrelevant threats.
                        </Box>
                      </SpaceBetween>
                    </Alert>
                  );
                })()}
                {title.length > 0 && (
                  <SpaceBetween size="xs">
                    <Header
                      variant="h3"
                      actions={<Button onClick={() => setActiveStepIndex(0)}>Edit</Button>}
                    >
                      Step 1: Title
                    </Header>
                    <Input
                      onChange={({ detail }) => setTitle(detail.value)}
                      value={title}
                      disabled
                    />
                  </SpaceBetween>
                )}
                {value[0] && (
                  <SpaceBetween size="xs">
                    <Header
                      variant="h3"
                      actions={<Button onClick={() => setActiveStepIndex(1)}>Edit</Button>}
                    >
                      Step 2: Architecture diagram
                    </Header>
                    <FileTokenGroup
                      i18nStrings={{
                        removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                        limitShowFewer: "Show fewer files",
                        limitShowMore: "Show more files",
                        errorIconAriaLabel: "Error",
                        warningIconAriaLabel: "Warning",
                      }}
                      items={[
                        {
                          file: value[0],
                        },
                      ]}
                      readOnly
                      showFileLastModified
                      showFileThumbnail
                      showFileSize
                    />
                  </SpaceBetween>
                )}
                {text.length > 0 && (
                  <SpaceBetween size="xs">
                    <Header
                      variant="h3"
                      actions={<Button onClick={() => setActiveStepIndex(2)}>Edit</Button>}
                    >
                      Step 3: Description
                    </Header>
                    <Textarea
                      onChange={({ detail }) => setText(detail.value)}
                      value={text}
                      placeholder="Add your description"
                      disabled
                    />
                  </SpaceBetween>
                )}
                {iteration && (
                  <SpaceBetween size="xs">
                    <Header
                      variant="h3"
                      actions={<Button onClick={() => setActiveStepIndex(3)}>Edit</Button>}
                    >
                      Step 4: Iterations
                    </Header>
                    <FormField>
                      <Select
                        disabled
                        options={[
                          { label: "1", value: "1" },
                          { label: "2", value: "2" },
                          { label: "3", value: "3" },
                          { label: "4", value: "4" },
                          { label: "5", value: "5" },
                        ]}
                        selectedOption={iteration}
                        triggerVariant="option"
                        onChange={({ detail }) => setIteration(detail.selectedOption)}
                      />
                    </FormField>
                    <Slider
                      i18nStrings={I18nProvider}
                      readOnly={true}
                      onChange={({ detail }) => setReasoning(detail.value)}
                      value={reasoning}
                      valueFormatter={(value) =>
                        [
                          { value: "0", label: "None" },
                          { value: "1", label: "Low" },
                          { value: "2", label: "Medium" },
                          { value: "3", label: "High" },
                        ].find((item) => item.value === value.toString())?.label || ""
                      }
                      ariaDescription="From None to High"
                      max={3}
                      min={0}
                      referenceValues={[1, 2]}
                      step={1}
                    />
                  </SpaceBetween>
                )}
                {assumptions.length > 0 && (
                  <SpaceBetween size="xs">
                    <Header
                      variant="h3"
                      actions={<Button onClick={() => setActiveStepIndex(4)}>Edit</Button>}
                    >
                      Step 5: Assumptions
                    </Header>
                    <FormField>
                      <TokenGroup items={convertArrayToObjects(assumptions)} readOnly />
                    </FormField>
                  </SpaceBetween>
                )}
              </SpaceBetween>
            </div>
          ),
        },
      ]}
    />
  );
};
